import React, { useEffect, useRef } from 'react';
import { RootState, useAppSelector } from '../../../../store';
interface VoiceEqualizerProps {
    volume: number;
    isRecording: boolean;
}

const VoiceEqualizer: React.FC<VoiceEqualizerProps> = ({ volume, isRecording }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const selectedTheme = useAppSelector((state: RootState) => (state as any).themes?.selectedTheme || '');
    const themes = useAppSelector((state: RootState) => (state as any).themes?.allThemes || {});
    const themeColor = themes?.[selectedTheme]?.themeColor ?? 'light';

    const drawRoundedRect = (
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        radius: number,
    ) => {
        if (typeof (ctx as any).roundRect === 'function') {
            ctx.beginPath();
            (ctx as any).roundRect(x, y, width, height, radius);
            ctx.fill();
        } else {
            ctx.beginPath();
            ctx.moveTo(x + radius, y);
            ctx.lineTo(x + width - radius, y);
            ctx.arcTo(x + width, y, x + width, y + radius, radius);
            ctx.lineTo(x + width, y + height - radius);
            ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
            ctx.lineTo(x + radius, y + height);
            ctx.arcTo(x, y + height, x, y + height - radius, radius);
            ctx.lineTo(x, y + radius);
            ctx.arcTo(x, y, x + radius, y, radius);
            ctx.closePath();
            ctx.fill();
        }
    };

    useEffect(() => {
        if (!canvasRef.current || !isRecording) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        canvas.width = canvas.clientWidth;
        canvas.height = canvas.clientHeight;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const barCount = 100;
        const barWidth = (canvas.width / barCount) * 0.7;
        const gap = (canvas.width - barWidth * barCount) / (barCount + 1);
        const maxBarHeight = canvas.height * 1;

        const gradient = ctx.createLinearGradient(0, canvas.height, 0, 0);

        if (themeColor === 'dark') {
            gradient.addColorStop(0, '#f3f4f6');
            gradient.addColorStop(0.7, '#e5e7eb');
            gradient.addColorStop(1, '#ffffff');
        } else {
            gradient.addColorStop(0, '#4b5563');
            gradient.addColorStop(0.7, '#374151');
            gradient.addColorStop(1, '#1f2937');
        }

        ctx.fillStyle = gradient;

        for (let i = 0; i < barCount; i++) {
            const normalizedVolume = volume / 56;
            const heightVariation = Math.random() * 0.5 + 0.5;
            const barHeight = Math.max(3, maxBarHeight * normalizedVolume * heightVariation);

            const x = gap + i * (barWidth + gap);
            const y = canvas.height - barHeight;

            drawRoundedRect(ctx, x, y, barWidth, barHeight, 3);
        }
    }, [volume, isRecording, themeColor]);

    return (
        <div className="w-full h-[24px] rounded-md overflow-hidden">
            <canvas ref={canvasRef} className="w-full h-full" />
        </div>
    );
};

export default VoiceEqualizer;
